import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Globe, Trash2, Ban, RefreshCw, Server } from "lucide-react";
import { sr } from "../../lib/sr";

const STATE = {
  not_configured: { label: "Not configured", bg: "#F1F5F9", fg: "#64748B" },
  action_required: { label: "Action required", bg: "#FEE2E2", fg: "#B91C1C" },
  awaiting_ownership: { label: "Awaiting ownership", bg: "#FEF3C7", fg: "#92400E" },
  certificate_provisioning: { label: "Cert provisioning", bg: "#DBEAFE", fg: "#1D4ED8" },
  checking_app: { label: "Checking app", bg: "#E0E7FF", fg: "#4338CA" },
  active: { label: "Active", bg: "#DCFCE7", fg: "#166534" },
  misconfigured: { label: "Misconfigured", bg: "#FEE2E2", fg: "#B91C1C" },
  disabled: { label: "Disabled", bg: "#F1F5F9", fg: "#64748B" },
};

function FallbackOriginCard() {
  const [fo, setFo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => sr.adminGetFallbackOrigin().then(setFo).catch(() => setFo({ configured: false }));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try { const r = await sr.adminSetFallbackOrigin(); toast.success(`Fallback origin set (${r.status || "ok"})`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not set fallback origin"); }
    finally { setBusy(false); }
  };
  return (
    <div className="sr-card" style={{ marginBottom: 16 }} data-testid="fallback-origin-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Server size={18} style={{ color: "var(--blue)" }} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <strong style={{ color: "var(--navy)" }}>Cloudflare fallback origin</strong>
          <div className="sr-muted" style={{ fontSize: 12 }}>
            {fo === null ? "Loading…" : fo.configured === false ? "Cloudflare not configured on the server."
              : fo.error ? <span style={{ color: "#B91C1C" }}>{fo.error}</span>
              : <>Current: <b>{fo.origin || "— not set —"}</b>{fo.status ? ` · ${fo.status}` : ""}</>}
          </div>
        </div>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={load} data-testid="fallback-refresh"><RefreshCw size={13} /></button>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={save} disabled={busy || (fo && fo.configured === false)} data-testid="fallback-set">{busy ? <Loader2 size={13} className="sr-spin" /> : "Set from env"}</button>
      </div>
    </div>
  );
}

export default function AdminDomains() {
  const [rows, setRows] = useState(null);
  const load = () => sr.adminDomains().then((r) => setRows(r.domains || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const disable = async (id) => { if (!window.confirm("Disable this domain? It stops serving its workspace.")) return; try { await sr.adminDisableDomain(id); toast.success("Disabled"); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete this mapping and remove the Cloudflare hostname?")) return; try { await sr.adminDeleteDomain(id); toast.success("Removed"); load(); } catch { toast.error("Failed"); } };

  return (
    <div style={{ maxWidth: 1100 }} data-testid="admin-domains-page">
      <h1 style={{ fontSize: 24, color: "var(--navy)", margin: "0 0 4px" }}><Globe size={22} style={{ verticalAlign: "-3px" }} /> Custom Domains (Admin)</h1>
      <p className="sr-muted" style={{ fontSize: 13, marginBottom: 16 }}>Every agency/client domain mapping, raw Cloudflare + certificate status, and live application health.</p>

      <FallbackOriginCard />

      {rows === null ? <div className="sr-empty"><Loader2 className="sr-spin" /></div>
        : rows.length === 0 ? <div className="sr-empty" data-testid="admin-domains-empty">No domains connected across any agency yet.</div>
        : (
          <div className="sr-card" style={{ padding: 0, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }} data-testid="admin-domains-table">
              <thead>
                <tr style={{ background: "#F8FAFC", textAlign: "left", color: "var(--muted)" }}>
                  {["Hostname", "Agency / Client", "State", "CF status", "SSL", "App", "Last check", "Actions"].map((h) => (
                    <th key={h} style={{ padding: "10px 12px", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const s = STATE[d.state] || STATE.not_configured;
                  return (
                    <tr key={d.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`admin-domain-${d.id}`}>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ fontWeight: 700, color: "var(--navy)" }}>{d.hostname}{d.is_primary ? " ★" : ""}</div>
                        <div className="sr-muted" style={{ fontSize: 11 }}>{d.scope}{d.cf_hostname_id ? ` · cf:${String(d.cf_hostname_id).slice(0, 8)}` : ""}</div>
                      </td>
                      <td style={{ padding: "10px 12px" }}>{d.agency_name || "—"}{d.client_name ? <div className="sr-muted" style={{ fontSize: 11 }}>{d.client_name}</div> : null}</td>
                      <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "3px 9px", background: s.bg, color: s.fg }}>{s.label}</span></td>
                      <td style={{ padding: "10px 12px" }}>{d.cf_status || "—"}</td>
                      <td style={{ padding: "10px 12px" }}>{d.cf_ssl_status || "—"}</td>
                      <td style={{ padding: "10px 12px" }}>{d.app_check ? (d.app_check.ok ? "✅" : "⏳") : "—"}</td>
                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }} className="sr-muted">{d.last_check_at ? String(d.last_check_at).slice(0, 16).replace("T", " ") : "—"}</td>
                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                        {d.state !== "disabled" && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => disable(d.id)} data-testid={`admin-domain-disable-${d.id}`} title="Disable"><Ban size={13} /></button>}
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ color: "#B91C1C" }} onClick={() => del(d.id)} data-testid={`admin-domain-delete-${d.id}`} title="Delete"><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
